import './Explore.sass';

import { FC, useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRoute } from 'react-router5';

import { Header, Tabs, TabsItem } from '$uikit';
import { exploreActions, exploreSelector } from '$store/explore';
import { useCurrentUserInfo, useTranslator } from '$hooks';
import { EXPLORE, FEED } from '$shared/constants/pages';
import router from '$navigation/router';
import { ExploreItems } from '$core/Explore/ExploreItems/ExploreItems';
import { ScrollView } from '$uikit/ScrollView/ScrollView';
import { mainSelector } from '$store/main';
import { feedActions, feedSelector } from '$store/feed';

export const Explore: FC = () => {
  const dispatch = useDispatch();
  const t = useTranslator();
  const { route } = useRoute();
  const routerState = router.getState();
  const { decryptKeys } = useSelector(mainSelector);
  const user = useCurrentUserInfo();
  const { isLoading, isFailed, tags, posts, isLoadingMore, nextFrom } =
    useSelector(exploreSelector);
  const { hasNewFollowingPosts } = useSelector(feedSelector);
  const selected = route.params.tag || '';
  const headerRef = useRef<HTMLDivElement>(null);
  const isReturnBack = useMemo(
    () => routerState.meta?.source === 'popstate',
    [routerState],
  );

  useEffect(() => {
    if (isReturnBack && posts.length) {
      return;
    }
    dispatch(exploreActions.loadExplore());
  }, []);

  const handleRetry = useCallback(() => {
    dispatch(exploreActions.loadExplore());
  }, []);

  const handleTag = useCallback((tag: string) => {
    const params: any = {};
    if (tag && tag !== 'all') {
      params.tag = tag;
    }
    router.navigate(EXPLORE, params, { replace: true });
    dispatch(exploreActions.loadExplore());
  }, []);

  const handleMore = useCallback(() => {
    dispatch(exploreActions.exploreLoadMore());
  }, []);

  const tagsPrepared = useMemo(() => {
    return [
      {
        name: 'all',
        count: 0,
      },
      ...tags,
    ];
  }, [tags]);

  const hasSponsorSubscription = useMemo(() => {
    for (const decryptKey in decryptKeys) {
      if (+decryptKey > 0 && +decryptKey !== user.id) {
        return true;
      }
    }

    return false;
  }, [decryptKeys]);

  const handleTabChange = (newTab) => {
    dispatch(feedActions.setActiveSection(newTab));

    if (newTab === 'explore') {
      return;
    }

    router.navigate(FEED, {
      section: newTab,
    });
  };

  return (
    <>
      <Header>{t('explore_title')}</Header>
      <ScrollView
        canLoadMore={!!nextFrom && !isLoadingMore}
        onLoadMore={handleMore}
        isLoadingMore={isLoadingMore}
      >
        <div ref={headerRef} className="Explore__header_container">
          <div className="Feed__tabs">
            <Tabs selected="explore" onChange={handleTabChange} skipMargin>
              <TabsItem value="smart">{t('feed_tab_default')}</TabsItem>
              <TabsItem value="following" badge={hasNewFollowingPosts}>
                {t('feed_tab_following')}
              </TabsItem>
              <TabsItem value="explore">{t('feed_tab_explore')}</TabsItem>
              {hasSponsorSubscription && (
                <TabsItem value="sponsored">{t('feed_tab_sponsored')}</TabsItem>
              )}
              <TabsItem value="liked">{t('feed_tab_liked')}</TabsItem>
              <TabsItem value="new">{t('feed_tab_new')}</TabsItem>
            </Tabs>
          </div>
        </div>
        <ExploreItems
          isLoading={isLoading}
          handleRetry={handleRetry}
          handleTag={handleTag}
          isFailed={isFailed}
          posts={posts}
          selected={selected}
          tagsPrepared={tagsPrepared}
        />
      </ScrollView>
    </>
  );
};
